import React, { useRef, useState, useCallback, useEffect } from "react";
import { Box, Typography, Button, Card, CardContent } from "@mui/material";
import { panService } from "../Services/PanService";
import { SessionStore } from "../Services/SessionStore";
import { auditLogService } from "../Services/AuditLogService";
import { useSelector } from "react-redux";

const CameraScreen = ({ onConfirm }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const timerRef = useRef(null);
    const streamRef = useRef(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [showReviewButtons, setShowReviewButtons] = useState(false);
    const [countdown, setCountdown] = useState(10);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const [isProcessingComplete, setIsProcessingComplete] = useState(false);
    const [cameraError, setCameraError] = useState("");
    const [isCountdownActive, setIsCountdownActive] = useState(false);
    const [cameraReady, setCameraReady] = useState(false);
    const customerSession = localStorage.getItem("session");

    const initCamera = useCallback(async () => {
        try {
            setCameraError("");
            setCameraReady(false);
            
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }

            const constraints = {
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: 'environment'
                }
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;
            
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => {
                    setCameraReady(true);
                    if (!isCountdownActive && !capturedImage) {
                        setTimeout(() => startCountdown(), 2000);
                    }
                };
            }
        } catch (error) {
            console.error('Camera error:', error);
            setCameraError(getCameraErrorMessage(error));
        }
    }, []);

    const getCameraErrorMessage = useCallback((error) => {
        if (error.name === 'NotAllowedError') {
            return 'Camera access denied. Please allow camera permission and refresh.';
        } else if (error.name === 'NotFoundError') {
            return 'No camera found. Please connect a camera device.';
        } else if (error.name === 'NotReadableError') {
            return 'Camera is being used by another application.';
        } else {
            return `Camera error: ${error.message}`;
        }
    }, []);

    const startCountdown = useCallback(() => {
        if (isCountdownActive || !cameraReady || capturedImage) return;
        
        setIsCountdownActive(true);
        clearInterval(timerRef.current);
        let left = 10;
        setCountdown(left);
        
        timerRef.current = setInterval(() => {
            left -= 1;
            setCountdown(left);
            if (left <= 0) {
                clearInterval(timerRef.current);
                setIsCountdownActive(false);
                handleCapture();
            }
        }, 1000);
    }, [isCountdownActive, cameraReady, capturedImage]);

    const captureImage = useCallback(() => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        
        if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
            console.error('Camera not ready');
            return null;
        }
        
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
        
        // PAN card aspect ratio is approximately 1.6:1 (85.6mm x 53.98mm)
        const panAspectRatio = 1.6;
        const maxWidth = videoWidth * 0.8;
        const maxHeight = videoHeight * 0.6;
        
        let boxWidth, boxHeight;
        if (maxWidth / panAspectRatio <= maxHeight) {
            boxWidth = maxWidth;
            boxHeight = maxWidth / panAspectRatio;
        } else {
            boxHeight = maxHeight;
            boxWidth = maxHeight * panAspectRatio;
        }
        
        const boxX = (videoWidth - boxWidth) / 2;
        const boxY = (videoHeight - boxHeight) / 2;
        
        canvas.width = boxWidth;
        canvas.height = boxHeight;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, boxX, boxY, boxWidth, boxHeight, 0, 0, boxWidth, boxHeight);
        
        return canvas.toDataURL('image/jpeg', 0.92);
    }, []);

    const dataURLtoBlob = useCallback((dataURL) => {
        const arr = dataURL.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime });
    }, []);

    const handleUpload = useCallback(async (blob) => {
        if (!blob) return;
        try {
            setIsUploading(true);
            setUploadError("");
            
            const storedSessionId = SessionStore.getSessionId();
            if (!storedSessionId) {
                setUploadError('Session ID not found. Please restart the KYC process.');
                return;
            }
            
            const requestId = `request_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
            
            const response = await panService.extractPan(blob, storedSessionId, requestId, customerSession);
            
            if (response?.message === "PAN_IMAGE is under process. Results will be stored in DB.") {
                setIsProcessingComplete(true);
            }
        } catch (e) {
            setUploadError(e?.response?.data?.message || e?.message || "Upload failed");
        } finally {
            setIsUploading(false);
        }
    }, []);
    
    const handleCapture = useCallback(async () => {
        clearInterval(timerRef.current);
        setIsCountdownActive(false);
        
        const dataUrl = captureImage();
        if (dataUrl) {
            setCapturedImage(dataUrl);
            setShowReviewButtons(true);
            const blob = dataURLtoBlob(dataUrl);
            await handleUpload(blob);
        }
    }, [captureImage, dataURLtoBlob, handleUpload]);

    const handleRetake = useCallback(() => {
        setCapturedImage(null);
        setShowReviewButtons(false);
        setUploadError("");
        setIsProcessingComplete(false);
        setCameraError("");
        setIsCountdownActive(false);
        clearInterval(timerRef.current);
        initCamera();
    }, [initCamera]);

    const handleLooksGood = useCallback(() => {
        if (onConfirm && isProcessingComplete) {
            onConfirm();
        }
    }, [onConfirm, isProcessingComplete]);

    useEffect(() => {
        initCamera();
        
        return () => {
            clearInterval(timerRef.current);
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    useEffect(() => {
        if (cameraReady && !isCountdownActive && !capturedImage) {
            startCountdown();
        }
    }, [cameraReady, isCountdownActive, capturedImage, startCountdown]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && !capturedImage) {
                setTimeout(() => initCamera(), 500);
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [capturedImage, initCamera]);

    return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "flex-start", mt: 4, px: 2 }}>
            <Card sx={{ width: "100%", maxWidth: "1200px", borderRadius: 3, boxShadow: "0px 4px 20px rgba(0,0,0,0.05)", p: { xs: 2, sm: 3 }, overflow: "hidden" }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        PAN Capture
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Please allow camera access to capture your PAN card.
                    </Typography>

                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2, borderRadius: 2, overflow: "hidden", background: "#000", width: "100%",  height: { xs: "260px", sm: "300px", md: "380px", lg: "450px" }, maxHeight: 500, position: "relative" }}>
                        {!capturedImage ? (
                            <>
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }}
                                />
                                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "60%", height: "60%", border: "3px solid #4CAF50", borderRadius: 2, pointerEvents: "none", zIndex: 10 }} />
                                <Box sx={{ position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)", backgroundColor: "rgba(0,0,0,0.7)", color: "white", px: 2, py: 1, borderRadius: 1, zIndex: 10 }}>
                                    <Typography variant="body2" textAlign="center">
                                        {isCountdownActive ? `Auto capture in ${countdown}s` : 'Position PAN card within the green box'}
                                    </Typography>
                                </Box>
                                {cameraError && (
                                    <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.8)" }}>
                                        <Typography color="error" textAlign="center" p={2}>
                                            {cameraError}
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <img src={capturedImage} alt="Captured PAN" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "8px" }} />
                        )}
                    </Box>

                    <canvas ref={canvasRef} style={{ display: "none" }} />

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {isCountdownActive ? `Capturing in ${countdown} seconds...` : cameraReady ? 'Camera ready - countdown will start automatically' : 'Starting camera...'}
                        </Typography>
                        {isUploading && (
                            <Typography variant="body2" color="text.secondary">
                                Uploading...
                            </Typography>
                        )}
                    </Box>

                    {uploadError && (
                        <Typography variant="body2" color="error" mt={1}>
                            {uploadError}
                        </Typography>
                    )}

                    {showReviewButtons && (
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
                            <Button size="small" variant="outlined" sx={{ textTransform: "none", borderRadius: 2, px: 2, minWidth: "auto" }} onClick={handleRetake} disabled={isUploading}>
                                Retake
                            </Button>
                            <Button size="small" variant="contained" sx={{ background: "#1976d2", "&:hover": { background: "#125ea2" }, textTransform: "none", borderRadius: 2, px: 2, minWidth: "auto" }} onClick={handleLooksGood} disabled={isUploading || !isProcessingComplete}>
                                Continue
                            </Button>
                        </Box>
                    )}


                </CardContent>
            </Card>
        </Box>
    );
};

export default CameraScreen;